import type { SelectSize, SelectState } from './select.types'

interface SizeTokens {
  trigger: string
  icon: string
  tag: string
  tagRemove: string
  counter: string
  search: string
  item: string
  itemIcon: string
  empty: string
}

export const sizeClasses: Record<SelectSize, SizeTokens> = {
  xs: {
    trigger: 'min-h-7 gap-1 rounded-md px-2 py-0.5 text-xs',
    icon: 'size-4',
    tag: 'h-4 gap-1 rounded px-1 text-[10px]',
    tagRemove: 'size-3',
    counter: 'h-4 min-w-4 px-1 text-[10px]',
    search: 'px-2 py-1 text-xs',
    item: 'gap-1.5 px-2 py-1 text-xs',
    itemIcon: 'size-4',
    empty: 'p-2 text-xs'
  },
  sm: {
    trigger: 'min-h-8 gap-1.5 rounded-md px-2.5 py-1 text-sm',
    icon: 'size-4',
    tag: 'h-5 gap-1 rounded px-1.5 text-xs',
    tagRemove: 'size-3.5',
    counter: 'h-5 min-w-5 px-1 text-xs',
    search: 'px-2.5 py-1.5 text-sm',
    item: 'gap-2 px-2.5 py-1.5 text-sm',
    itemIcon: 'size-4',
    empty: 'p-3 text-sm'
  },
  md: {
    trigger: 'gap-2 px-3 py-3 text-sm',
    icon: 'size-5',
    tag: 'h-6 gap-1 rounded-md px-2 text-xs',
    tagRemove: 'size-4',
    counter: 'h-6 min-w-6 px-1.5 text-xs',
    search: 'px-3 py-2 text-sm',
    item: 'gap-2 px-3 py-2 text-sm',
    itemIcon: 'size-5',
    empty: 'p-4 text-sm'
  },
  lg: {
    trigger: 'min-h-[46px] gap-2 rounded-md px-4 py-2.5 text-base',
    icon: 'size-5',
    tag: 'h-7 gap-1 rounded-md px-2 text-sm',
    tagRemove: 'size-4',
    counter: 'h-7 min-w-7 px-2 text-sm',
    search: 'px-4 py-2.5 text-base',
    item: 'gap-2.5 px-3.5 py-2.5 text-base',
    itemIcon: 'size-5',
    empty: 'p-5 text-base'
  },
  xl: {
    trigger: 'min-h-[54px] gap-3 rounded-md px-5 py-3 text-lg',
    icon: 'size-6',
    tag: 'h-8 gap-1.5 rounded-md px-2.5 text-sm',
    tagRemove: 'size-4.5',
    counter: 'h-8 min-w-8 px-2.5 text-sm',
    search: 'px-5 py-3 text-lg',
    item: 'gap-3 px-4 py-3 text-lg',
    itemIcon: 'size-6',
    empty: 'p-6 text-lg'
  }
}

export const triggerBase = 'relative flex w-full min-w-0 cursor-pointer items-center overflow-hidden rounded-md border text-[#212121] outline-none transition-all focus-visible:ring-2 focus-visible:ring-[--c-primary-400] focus-visible:ring-offset-0 dark:text-[#FDFDFD]'

export const stateClasses: Record<SelectState | 'disabled', string> = {
  default: 'border-neutral-400/50 bg-[#fdfdfd] focus:border-[--c-primary-600] focus:bg-[#f6f2f9] focus-within:border-[--c-primary-600] hover:bg-[#f8f6fb] dark:border-[#2C2C2C] dark:bg-[#2C2C2C] dark:focus:border-[--c-primary-600] dark:focus:bg-[#383838] dark:hover:bg-[#343434]',
  success: 'border-emerald-400 bg-emerald-50/60 focus:border-emerald-500 focus-within:border-emerald-500 dark:bg-transparent',
  error: 'border-[#FF4980] bg-[#fcf2f6] focus:border-[#FF4980] focus-within:border-[#FF4980] dark:bg-[#383838]',
  blocked: 'cursor-not-allowed border-neutral-300 bg-neutral-100 text-neutral-500 dark:border-neutral-700 dark:bg-[#383838] dark:text-neutral-400',
  disabled: 'cursor-not-allowed border-neutral-300 bg-[#e6e1e6] text-[#79767A] opacity-70 dark:border-[#2C2C2C] dark:bg-[#414141] dark:text-[#9C9C9C]'
}

export const itemClasses = 'group relative flex w-full cursor-pointer select-none items-center rounded-md text-[#666666] outline-none transition-colors hover:bg-neutral-200/60 dark:text-white dark:hover:bg-neutral-600'
export const itemDisabledClasses = 'cursor-not-allowed opacity-50 hover:bg-transparent dark:hover:bg-transparent'
export const itemActiveClasses = 'bg-[--c-primary-300] text-[--c-primary-700] dark:bg-[--c-primary-700] dark:text-white'
export const itemLabelClasses = 'truncate font-semibold text-neutral-500 dark:text-neutral-400'
export const itemDescriptionClasses = 'truncate text-xs text-neutral-400 dark:text-neutral-500'
export const separatorClasses = 'my-1 h-px bg-neutral-200 dark:bg-neutral-700'
